import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Observable, map, take } from 'rxjs';
import { getSessionAuthData } from '../states/session-auth/session-auth.selectors';
import { sessionAuthData } from '../interfaces/states-data';

/**
 * C2-SEC-V1: rutas que deben permitir acceso AUNQUE el usuario esté logueado.
 * El link de cambio de email llega al usuario (vía correo) cuando puede estar
 * con sesión abierta — el guard genérico de "/auth/*" lo redirigiría a /home
 * y el token nunca se procesaría. Estas rutas son `@Public()` del lado backend
 * y deben aceptarse aquí también.
 */
// Los patrones anclan en `^\/` porque `RouterStateSnapshot.url` en Angular
// siempre es una ruta root-relative (nunca una URL absoluta con protocolo/host).
const ALLOW_WHEN_LOGGED_IN_PATTERNS: RegExp[] = [
  /^\/auth\/confirm-email-change\//,
  /^\/auth\/cancel-email-change\//,
];

export const NoSessionAuthGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree => {

  const store = inject(Store<any>);
  const router = inject(Router);

  // C2-SEC-V1: bypass del guard para rutas que aceptan sesión activa.
  if (ALLOW_WHEN_LOGGED_IN_PATTERNS.some((rx) => rx.test(state.url))) {
    return true;
  }

  const invalidRoute = route.data['invalidRoute'] ? route.data['invalidRoute'] : 'home';

  return store.pipe(
    select(getSessionAuthData),
    take(1),
    map((data: sessionAuthData) => {
      if (!data.loggedIn) {
        return !data.loggedIn;
      } else {
        router.navigate([invalidRoute]);
        return !data.loggedIn;
      }
    })
  );
};
